import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { GoogleMap, useJsApiLoader, Marker, Polyline, Circle, InfoWindow } from "@react-google-maps/api";
import { API , authFetch } from "@/App";
import {
  MapPin, Navigation, Battery, Clock, RefreshCw, Calendar,
  Fingerprint, Route, Play, Pause, ChevronLeft, ChevronRight,
  Phone, Store, Coffee, ArrowRight, Gauge, SkipForward, SkipBack,
  Zap, Search, Users, ChevronDown, MapPinned
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const mapContainerStyle = { width: "100%", height: "100%", borderRadius: "0 0 12px 12px" };
const ROLE_COLORS = { AREA_MANAGER: "#8B1A1A", SALES_EXECUTIVE: "#3B82F6", PROMOTOR: "#10B981" };
const SEGMENT_COLORS = { TRAVEL: "#3B82F6", AT_DEALER: "#F59E0B", STATIONARY: "#EF4444", CHECKED_IN: "#10B981", CHECKED_OUT: "#EF4444" };

const EVENT_CONFIG = {
  CHECK_IN: { icon: Fingerprint, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Check In" },
  TRAVEL: { icon: Route, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", label: "Travel" },
  AT_DEALER: { icon: Store, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Dealer Visit" },
  STOPPAGE: { icon: Coffee, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Stoppage" },
  CHECK_OUT: { icon: Fingerprint, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Check Out" },
};

const formatTime = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }); }
  catch { return ts; }
};
const formatHHMM = (timeStr) => {
  if (!timeStr) return "—";
  const parts = timeStr.split(":");
  if (parts.length < 2) return timeStr;
  const h = parseInt(parts[0]), m = parts[1];
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
};

/* ═══════════════════ EMPLOYEE LIST PANEL ═══════════════════ */
const EmployeeListPanel = ({ employees, selectedId, onSelect, loading }) => {
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("ALL");

  const regions = [...new Set(employees.map(e => e.region_id).filter(Boolean))];
  const filtered = employees.filter(e => {
    const matchSearch = !search || e.full_name?.toLowerCase().includes(search.toLowerCase()) || e.employee_code?.toLowerCase().includes(search.toLowerCase());
    const matchRegion = regionFilter === "ALL" || e.region_id === regionFilter;
    return matchSearch && matchRegion;
  });

  return (
    <div className="flex flex-col h-full" data-testid="employee-list-panel">
      {/* Search + Filter */}
      <div className="p-3 border-b border-slate-100 space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input placeholder="Search employee..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs" data-testid="employee-search" />
        </div>
        {regions.length > 1 && (
          <div className="flex gap-1">
            <button onClick={() => setRegionFilter("ALL")}
              className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${regionFilter === "ALL" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid="region-filter-all">All</button>
            {regions.map(r => (
              <button key={r} onClick={() => setRegionFilter(r)}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${regionFilter === r ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`region-filter-${r}`}>{r}</button>
            ))}
          </div>
        )}
      </div>
      {/* Employee List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-xs">No employees found</div>
        ) : (
          filtered.map(emp => {
            const isActive = emp.employee_id === selectedId;
            return (
              <button key={emp.employee_id} onClick={() => onSelect(emp.employee_id)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-50 transition-all hover:bg-slate-50 ${isActive ? "bg-[#8B1A1A]/5 border-l-2 border-l-[#8B1A1A]" : ""}`}
                data-testid={`employee-item-${emp.employee_id}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                    style={{ backgroundColor: ROLE_COLORS[emp.role] || "#6B7280" }}>
                    {emp.full_name?.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-semibold truncate ${isActive ? "text-[#8B1A1A]" : "text-slate-800"}`}>{emp.full_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[9px] font-mono text-slate-400">{emp.employee_code}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                      <span className="text-[9px] text-slate-400">{emp.role?.replace(/_/g, " ")}</span>
                    </div>
                  </div>
                  {emp.region_id && (
                    <Badge variant="outline" className="text-[8px] px-1 py-0 shrink-0">{emp.region_id}</Badge>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
      <div className="px-3 py-2 border-t border-slate-100 text-[10px] text-slate-400 font-mono">
        {filtered.length} of {employees.length} employees
      </div>
    </div>
  );
};

/* ═══════════════════ MAIN 360 VIEW ═══════════════════ */
const Employee360View = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeId = searchParams.get("id");
  const navigate = useNavigate();

  // Employee list state
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(true);

  // Timeline state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [mapError, setMapError] = useState(false);
  const [playbackIdx, setPlaybackIdx] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [activeInfo, setActiveInfo] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [geofences, setGeofences] = useState([]);
  const [showGeofences, setShowGeofences] = useState(true);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
  });

  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);

  // Fetch employee list
  useEffect(() => {
    const fetchEmployees = async () => {
      setEmpLoading(true);
      try {
        const res = await authFetch(`${API}/employees`, {  });
        const json = await res.json();
        if (json.success) setEmployees(json.data || []);
      } catch (err) { console.error(err); }
      finally { setEmpLoading(false); }
    };
    fetchEmployees();
  }, []);

  // Fetch timeline for selected employee
  const fetchTimeline = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await authFetch(`${API}/field-force/timeline/${employeeId}?date=${selectedDate}`, {  });
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [employeeId, selectedDate]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  // Fetch geofence settings once (P2 iter81)
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/settings/geofence`);
        const j = await res.json();
        if (j.success) setGeofences(j.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Playback animation
  useEffect(() => {
    if (!isPlaying || !data?.gps_trail?.length) return;
    const speeds = { 1: 150, 2: 75, 4: 35 };
    const interval = setInterval(() => {
      setPlaybackIdx((prev) => {
        if (prev >= data.gps_trail.length - 1) { setIsPlaying(false); return prev; }
        return prev + 1;
      });
    }, speeds[playbackSpeed] || 150);
    return () => clearInterval(interval);
  }, [isPlaying, data, playbackSpeed]);

  const selectEmployee = (id) => {
    setSearchParams({ id });
    resetPlayback();
    setData(null);
  };

  const changeDate = (delta) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().split("T")[0]);
    resetPlayback();
  };
  const resetPlayback = () => { setPlaybackIdx(-1); setIsPlaying(false); };
  const startPlayback = () => { if (playbackIdx < 0) setPlaybackIdx(0); setIsPlaying(true); };

  const buildSegmentPolylines = (trail) => {
    if (!trail?.length) return [];
    const polylines = [];
    let currentType = trail[0].segment_type;
    let currentPath = [{ lat: trail[0].lat, lng: trail[0].lng }];
    for (let i = 1; i < trail.length; i++) {
      const p = trail[i];
      if (p.segment_type === currentType) {
        currentPath.push({ lat: p.lat, lng: p.lng });
      } else {
        currentPath.push({ lat: p.lat, lng: p.lng });
        polylines.push({ type: currentType, path: [...currentPath] });
        currentType = p.segment_type;
        currentPath = [{ lat: p.lat, lng: p.lng }];
      }
    }
    if (currentPath.length > 1) polylines.push({ type: currentType, path: currentPath });
    return polylines;
  };

  const showMap = isLoaded && !loadError && !mapError;

  // Detail view content
  const renderDetailView = () => {
    if (!employeeId) return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3" data-testid="no-employee-selected">
        <MapPinned className="w-12 h-12 text-slate-300" />
        <p className="text-sm font-medium">Select an employee</p>
        <p className="text-xs">Choose from the list to view their 360° timeline</p>
      </div>
    );

    if (loading) return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
      </div>
    );

    if (!data) return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
        <MapPin className="w-10 h-10" />
        <p className="text-sm">No data available for this employee</p>
      </div>
    );

    const { employee, timeline, gps_trail, summary } = data;
    const mapCenter = gps_trail?.length > 0 ? { lat: gps_trail[0].lat, lng: gps_trail[0].lng } : { lat: 23.0, lng: 72.6 };
    const playbackPoint = playbackIdx >= 0 && gps_trail?.[playbackIdx] ? gps_trail[playbackIdx] : null;
    const segmentPolylines = buildSegmentPolylines(gps_trail);
    const dealerMarkers = timeline.filter(t => t.type === "AT_DEALER" && t.lat);
    const stoppageMarkers = timeline.filter(t => t.type === "STOPPAGE" && t.lat);

    const kpis = [
      { label: "Check In", value: formatHHMM(summary.check_in_time), icon: Clock, accent: "text-emerald-600", bg: "bg-emerald-50" },
      { label: "Distance", value: `${summary.total_distance_km} km`, icon: Route, accent: "text-blue-600", bg: "bg-blue-50" },
      { label: "Dealers", value: summary.dealers_visited, icon: Store, accent: "text-amber-600", bg: "bg-amber-50" },
      { label: "Avg Speed", value: `${summary.avg_speed} km/h`, icon: Gauge, accent: "text-violet-600", bg: "bg-violet-50" },
      { label: "Hours", value: `${summary.hours_on_field}h`, icon: Zap, accent: "text-[#8B1A1A]", bg: "bg-red-50" },
      { label: "Battery", value: summary.battery_level != null ? `${summary.battery_level}%` : "—", icon: Battery, accent: summary.battery_level > 20 ? "text-emerald-600" : "text-red-600", bg: summary.battery_level > 20 ? "bg-emerald-50" : "bg-red-50" },
    ];

    return (
      <div className="flex-1 flex flex-col gap-3 overflow-hidden">
        {/* Header row: employee info + date nav */}
        <div className="flex items-center justify-between flex-wrap gap-3 shrink-0 px-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
              style={{ backgroundColor: ROLE_COLORS[employee.role] || "#6B7280" }}>
              {employee.full_name?.charAt(0)}
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 font-['Sora']" data-testid="emp-name">{employee.full_name}</h2>
              <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                <span className="font-mono">{employee.employee_code}</span>
                <Badge variant="outline" className="text-[9px] px-1 py-0">{employee.role?.replace(/_/g, " ")}</Badge>
                {employee.mobile_number && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{employee.mobile_number}</span>}
                {employee.region_id && <Badge variant="outline" className="text-[9px] px-1 py-0">{employee.region_id}</Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => changeDate(-1)} className="p-1 rounded-md hover:bg-slate-100" data-testid="prev-date-btn"><ChevronLeft className="w-4 h-4 text-slate-500" /></button>
            <div className="flex items-center gap-1 bg-slate-50 rounded-md px-2 py-1 border border-slate-200">
              <Calendar className="w-3 h-3 text-slate-400" />
              <Input type="date" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); resetPlayback(); }}
                className="w-[120px] h-5 text-[11px] border-0 bg-transparent p-0 focus-visible:ring-0" data-testid="date-picker" />
            </div>
            <button onClick={() => changeDate(1)} className="p-1 rounded-md hover:bg-slate-100" data-testid="next-date-btn"><ChevronRight className="w-4 h-4 text-slate-500" /></button>
            <button onClick={() => { setSelectedDate(new Date().toISOString().split("T")[0]); resetPlayback(); }}
              className="px-2 py-0.5 text-[10px] font-medium bg-slate-100 rounded-md hover:bg-slate-200 text-slate-600" data-testid="today-btn">Today</button>
            <button onClick={fetchTimeline} className="p-1 rounded-md hover:bg-slate-100" data-testid="refresh-btn"><RefreshCw className="w-3 h-3 text-slate-400" /></button>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-6 gap-2 shrink-0" data-testid="stats-row">
          {kpis.map((kpi, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-2.5 hover:border-slate-300 transition-colors" data-testid={`stat-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-md ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon className={`w-3.5 h-3.5 ${kpi.accent}`} />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{kpi.label}</p>
                  <p className="text-xs font-bold text-slate-900 font-mono">{kpi.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Playback Controls */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2.5 shrink-0" data-testid="playback-controls">
          <div className="flex items-center gap-1">
            <button onClick={() => { setPlaybackIdx(0); setIsPlaying(false); }} className="p-1 rounded hover:bg-slate-100" data-testid="playback-start"><SkipBack className="w-3 h-3 text-slate-500" /></button>
            <button onClick={() => isPlaying ? setIsPlaying(false) : startPlayback()}
              className={`flex items-center gap-1 px-3 py-1 rounded-md text-[11px] font-semibold transition-all ${isPlaying ? "bg-red-50 text-red-700 border border-red-200" : "bg-[#8B1A1A] text-white hover:bg-[#6B1515]"}`}
              data-testid="playback-btn">
              {isPlaying ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Play</>}
            </button>
            <button onClick={() => { setPlaybackIdx(gps_trail?.length - 1 || 0); setIsPlaying(false); }} className="p-1 rounded hover:bg-slate-100" data-testid="playback-end"><SkipForward className="w-3 h-3 text-slate-500" /></button>
          </div>
          <div className="flex items-center gap-0.5 bg-slate-100 rounded p-0.5" data-testid="speed-control">
            {[1, 2, 4].map(s => (
              <button key={s} onClick={() => setPlaybackSpeed(s)}
                className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${playbackSpeed === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                data-testid={`speed-${s}x`}>{s}x</button>
            ))}
          </div>
          {gps_trail?.length > 0 && (
            <div className="flex-1 flex items-center gap-2">
              <input type="range" min="0" max={gps_trail.length - 1} value={Math.max(playbackIdx, 0)}
                onChange={e => { setPlaybackIdx(parseInt(e.target.value)); setIsPlaying(false); }}
                className="flex-1 h-1 appearance-none rounded-full bg-slate-200 cursor-pointer accent-[#8B1A1A]" data-testid="playback-slider" />
              <span className="text-[9px] font-mono text-slate-400">{Math.max(playbackIdx + 1, 0)}/{gps_trail.length}</span>
            </div>
          )}
          {playbackPoint && (
            <div className="hidden lg:flex items-center gap-1.5 text-[9px] text-slate-500 border-l border-slate-200 pl-2.5">
              <span className="font-mono">{formatTime(playbackPoint.timestamp)}</span>
              <span className="font-mono">{playbackPoint.speed} km/h</span>
              {playbackPoint.battery_pct != null && <span className="flex items-center gap-0.5"><Battery className="w-2.5 h-2.5" />{playbackPoint.battery_pct}%</span>}
              {playbackPoint.dealer_name && <span className="text-amber-700 font-medium">{playbackPoint.dealer_name}</span>}
            </div>
          )}
        </div>

        {/* Timeline + Map */}
        <div className="flex-1 grid grid-cols-12 gap-3 min-h-0">
          {/* Timeline */}
          <div className="col-span-4 xl:col-span-3 bg-white border border-slate-200 rounded-lg flex flex-col overflow-hidden" data-testid="timeline-panel">
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h3 className="text-[10px] font-semibold text-slate-900 uppercase tracking-wider">Timeline</h3>
              <span className="text-[9px] font-mono text-slate-400">{timeline.length} events</span>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
              {timeline.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 py-6">
                  <Clock className="w-6 h-6 mb-1" />
                  <p className="text-[10px]">No activity</p>
                </div>
              ) : timeline.map((event, idx) => {
                const cfg = EVENT_CONFIG[event.type] || EVENT_CONFIG.TRAVEL;
                const Icon = cfg.icon;
                return (
                  <div key={idx}
                    className={`flex gap-2 cursor-pointer rounded-md px-1 py-0.5 transition-all ${hoveredEvent === idx ? "bg-slate-50" : ""}`}
                    data-testid={`timeline-event-${idx}`}
                    onMouseEnter={() => setHoveredEvent(idx)}
                    onMouseLeave={() => setHoveredEvent(null)}
                    onClick={() => { if (event.lat && mapRef.current) { mapRef.current.panTo({ lat: event.lat, lng: event.lng }); mapRef.current.setZoom(15); } }}>
                    <div className="flex flex-col items-center w-6 shrink-0">
                      <div className={`w-6 h-6 rounded-full ${cfg.bg} border ${cfg.border} flex items-center justify-center`}>
                        <Icon className={`w-3 h-3 ${cfg.color}`} />
                      </div>
                      {idx < timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 my-0.5 min-h-[8px]" />}
                    </div>
                    <div className="flex-1 min-w-0 pb-1.5">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-[11px] font-semibold text-slate-800">{cfg.label}</span>
                        {event.distance_km > 0 && <Badge variant="outline" className="text-[8px] px-1 py-0 text-blue-600 border-blue-200">{event.distance_km} km</Badge>}
                        {event.duration_min > 0 && event.type !== "CHECK_IN" && event.type !== "CHECK_OUT" && (
                          <Badge variant="outline" className="text-[8px] px-1 py-0">{event.duration_min} min</Badge>
                        )}
                      </div>
                      {event.dealer_name && <p className="text-[10px] font-medium text-amber-700 mt-0.5 flex items-center gap-0.5"><Store className="w-2.5 h-2.5" />{event.dealer_name}</p>}
                      <p className="text-[9px] text-slate-400 font-mono mt-0.5">
                        {formatTime(event.timestamp)}
                        {event.end_time && event.end_time !== event.timestamp && <span> <ArrowRight className="w-2 h-2 inline" /> {formatTime(event.end_time)}</span>}
                      </p>
                      {event.avg_speed != null && event.type === "TRAVEL" && <p className="text-[9px] text-slate-400">Avg: {event.avg_speed} km/h</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50 shrink-0">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[9px]">
                <div><span className="text-slate-400">In:</span> <span className="font-mono font-medium text-slate-700">{formatHHMM(summary.check_in_time)}</span></div>
                <div><span className="text-slate-400">Out:</span> <span className="font-mono font-medium text-slate-700">{formatHHMM(summary.check_out_time)}</span></div>
                <div><span className="text-slate-400">Travel:</span> <span className="font-mono font-medium text-slate-700">{summary.total_travel_minutes} min</span></div>
                <div><span className="text-slate-400">Stops:</span> <span className="font-mono font-medium text-slate-700">{summary.total_stoppage_minutes} min</span></div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="col-span-8 xl:col-span-9 bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col" data-testid="map-panel">
            <div className="px-3 py-1.5 border-b border-slate-100 flex items-center gap-3 shrink-0 flex-wrap">
              <h3 className="text-[10px] font-semibold text-slate-900 uppercase tracking-wider">Route Map</h3>
              <div className="flex items-center gap-2 text-[9px] text-slate-500">
                <span className="flex items-center gap-0.5"><span className="w-2.5 h-0.5 bg-blue-500 rounded-full" />Travel</span>
                <span className="flex items-center gap-0.5"><span className="w-2.5 h-0.5 bg-amber-500 rounded-full" />Dealer</span>
                <span className="flex items-center gap-0.5"><span className="w-2.5 h-0.5 bg-red-500 rounded-full" />Stop</span>
                <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Start</span>
                <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-red-500" />End</span>
                {geofences.length > 0 && (
                  <span className="flex items-center gap-0.5"><span className="w-2.5 h-2.5 rounded-full border-2 border-violet-500 bg-violet-500/20" />Geofence</span>
                )}
              </div>
              {geofences.length > 0 && (
                <label className="ml-auto flex items-center gap-1 text-[9px] cursor-pointer select-none" data-testid="toggle-geofences">
                  <input
                    type="checkbox"
                    checked={showGeofences}
                    onChange={(e) => setShowGeofences(e.target.checked)}
                    className="accent-violet-600 w-3 h-3"
                  />
                  <span className="text-slate-600 font-medium">Show {geofences.length} geofence{geofences.length !== 1 ? "s" : ""}</span>
                </label>
              )}
            </div>
            <div className="flex-1">
              {showMap ? (
                <GoogleMap mapContainerStyle={mapContainerStyle}
                  center={playbackPoint ? { lat: playbackPoint.lat, lng: playbackPoint.lng } : mapCenter} zoom={12}
                  onLoad={map => { mapRef.current = map; }}
                  options={{ styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }, { featureType: "transit", stylers: [{ visibility: "off" }] }], mapTypeControl: true, streetViewControl: false, fullscreenControl: true }}>
                  {/* Geofence circles overlay (P2 iter81) */}
                  {showGeofences && geofences.map((g, i) => {
                    if (g.lat == null || g.lng == null) return null;
                    return (
                      <Circle
                        key={`gf-${g.geofence_id || i}`}
                        center={{ lat: g.lat, lng: g.lng }}
                        radius={g.radius_meters || g.radius || 200}
                        options={{
                          strokeColor: "#8B5CF6",
                          strokeOpacity: 0.7,
                          strokeWeight: 1.5,
                          fillColor: "#8B5CF6",
                          fillOpacity: 0.10,
                          clickable: true,
                          zIndex: 1,
                        }}
                        onClick={() => setActiveInfo(activeInfo === `gf-${i}` ? null : `gf-${i}`)}
                      />
                    );
                  })}                  {/* Segment polylines */}
                  {segmentPolylines.map((seg, i) => (
                    <Polyline key={`seg-${i}`} path={seg.path}
                      options={{
                        strokeColor: SEGMENT_COLORS[seg.type] || "#94A3B8",
                        strokeOpacity: playbackIdx >= 0 ? 0.25 : 0.8,
                        strokeWeight: seg.type === "TRAVEL" ? 3 : 2,
                        ...(seg.type !== "TRAVEL" ? { icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 }, offset: "0", repeat: "12px" }] } : {}),
                      }} />
                  ))}
                  {/* Played trail */}
                  {playbackIdx > 0 && gps_trail && (
                    <Polyline path={gps_trail.slice(0, playbackIdx + 1).map(p => ({ lat: p.lat, lng: p.lng }))}
                      options={{ strokeColor: "#8B1A1A", strokeOpacity: 1, strokeWeight: 4, zIndex: 10 }} />
                  )}
                  {/* Start marker */}
                  {gps_trail?.length > 0 && (
                    <Marker position={{ lat: gps_trail[0].lat, lng: gps_trail[0].lng }}
                      icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 8, fillColor: "#10B981", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }}
                      title="Check-in" zIndex={5} />
                  )}
                  {/* End marker */}
                  {gps_trail?.length > 1 && (
                    <Marker position={{ lat: gps_trail[gps_trail.length - 1].lat, lng: gps_trail[gps_trail.length - 1].lng }}
                      icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 8, fillColor: "#EF4444", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }}
                      title="Check-out" zIndex={5} />
                  )}
                  {/* Dealer markers */}
                  {dealerMarkers.map((dm, i) => (
                    <Marker key={`d-${i}`} position={{ lat: dm.lat, lng: dm.lng }}
                      icon={{ path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW || 3, scale: 7, fillColor: "#F59E0B", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }}
                      title={`${dm.dealer_name} (${dm.duration_min} min)`} zIndex={6}
                      onClick={() => setActiveInfo(activeInfo === `d-${i}` ? null : `d-${i}`)}>
                      {activeInfo === `d-${i}` && (
                        <InfoWindow onCloseClick={() => setActiveInfo(null)}>
                          <div className="text-xs p-1">
                            <p className="font-semibold text-amber-700">{dm.dealer_name}</p>
                            <p className="text-slate-500 mt-0.5">{dm.duration_min} min visit</p>
                            <p className="text-slate-400 font-mono">{formatTime(dm.timestamp)}</p>
                          </div>
                        </InfoWindow>
                      )}
                    </Marker>
                  ))}
                  {/* Stoppages */}
                  {stoppageMarkers.map((sm, i) => (
                    <Marker key={`s-${i}`} position={{ lat: sm.lat, lng: sm.lng }}
                      icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 6, fillColor: "#EF4444", fillOpacity: 0.7, strokeColor: "#fff", strokeWeight: 1.5 }}
                      title={`Stoppage: ${sm.duration_min} min`} zIndex={4} />
                  ))}
                  {/* Playback marker */}
                  {playbackPoint && (
                    <Marker position={{ lat: playbackPoint.lat, lng: playbackPoint.lng }}
                      icon={{ path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 1, scale: 9, fillColor: "#8B1A1A", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 }}
                      zIndex={20} title={`${playbackPoint.speed} km/h`} />
                  )}
                </GoogleMap>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400 text-xs">
                  <div className="text-center"><MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p>Map loading... {gps_trail?.length || 0} points</p></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex gap-0 h-[calc(100vh-80px)]" data-testid="employee-360-view">
      {/* Left: Employee List */}
      <div className="w-[240px] shrink-0 bg-white border-r border-slate-200 flex flex-col">
        <div className="px-3 py-2.5 border-b border-slate-200 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#8B1A1A]" />
          <h3 className="text-xs font-bold text-slate-900 font-['Sora']">Field Force</h3>
        </div>
        <EmployeeListPanel
          employees={employees}
          selectedId={employeeId}
          onSelect={selectEmployee}
          loading={empLoading}
        />
      </div>
      {/* Right: 360 Detail View */}
      <div className="flex-1 flex flex-col p-3 overflow-hidden">
        {renderDetailView()}
      </div>
    </div>
  );
};

export default Employee360View;
